import type { StoreApi, UseBoundStore } from 'zustand';
import type { Locale } from '@/i18n/config';
import { createTrackedStore } from '@/lib/dev/zustand-registry';
import { logger } from '@/lib/telemetry/logger';

type OnboardingStage = 1 | 2 | 3 | 4 | 5 | 6 | 7;
type OnboardingSubScreen = '1' | '2' | '3' | '4' | '5' | '6' | '7';

export interface OnboardingState {
  stage: OnboardingStage;
  subScreen: OnboardingSubScreen;
  startTime: number;
  stageStartTime: number;

  pendingLocale: Locale | null;
  pendingTheme: 'light' | 'dark' | null;
  pendingCurrency: string | null;
  pendingCountry: string | null;

  displayName: string;
  handle: string;
  avatarUrl: string | null;
  heroUrl: string | null;
  phoneNumber: string;

  interestTopics: string[];
  interestPlatforms: string[];

  setSubScreen: (sub: OnboardingSubScreen) => void;
  advanceToStage2: () => void;
  advanceToStage3: () => void;
  advanceToStage4: () => void;
  advanceToStage5: () => void;
  advanceToStage6: () => void;
  advanceToStage7: () => void;
  goBackToStage1: () => void;
  goBackToStage2: () => void;
  goBackToStage3: () => void;
  goBackToStage4: () => void;
  goBackToStage5: () => void;
  completeOnboarding: () => void;
  setPendingLocale: (locale: Locale) => void;
  setPendingTheme: (theme: 'light' | 'dark') => void;
  setPendingCurrency: (code: string) => void;
  setPendingCountry: (code: string) => void;
  setDisplayName: (name: string) => void;
  setHandle: (handle: string) => void;
  setAvatarUrl: (url: string | null) => void;
  setHeroUrl: (url: string | null) => void;
  setPhoneNumber: (phone: string) => void;
  toggleInterestTopic: (topicId: string) => void;
  toggleInterestPlatform: (platformId: string) => void;
  setInterestTopics: (topics: string[]) => void;
  setInterestPlatforms: (platforms: string[]) => void;
  reset: () => void;
  rehydratePersistedProgress: () => void;
}

const STORAGE_PREFIX_STAGE = 'onboarding-stage';
const STORAGE_PREFIX_COMPLETED = 'onboarding-completed';
const PERSISTED_ONBOARDING_VERSION = 3;

/** Get the current user ID from localStorage (set by auth-success-handler). */
function getCurrentUserId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return localStorage.getItem('morfi_user_id');
  } catch {
    // SILENT-CATCH: localStorage unavailable in SSR or private browsing
    return null;
  }
}

function stageKey(): string {
  const uid = getCurrentUserId();
  return uid ? `${STORAGE_PREFIX_STAGE}:${uid}` : STORAGE_PREFIX_STAGE;
}

function completedKey(): string {
  const uid = getCurrentUserId();
  return uid ? `${STORAGE_PREFIX_COMPLETED}:${uid}` : STORAGE_PREFIX_COMPLETED;
}

/**
 * Persisted shape — survives full-page reloads (mid-onboarding refresh,
 * Safari OAuth-connect redirect through /auth/callback, etc.).
 *
 * Includes both stage progress AND the user-fillable form fields. Without
 * the form fields, a Safari user who completes Stage 2 and then triggers
 * an OAuth connect on Stage 3 would land back on Stage 3 (good) but with
 * empty displayName/handle in the store (bad — Stage 2's "Back" would show
 * empty fields, and Stage 3's onConnected callback can't merge imported
 * profile data into something that already exists).
 *
 * Stage-1 `pending*` fields are intentionally NOT persisted — they're
 * already mirrored to `next-themes`, the locale cookie, the currency
 * store, and the user profile via updateLocation() on Continue.
 */
interface PersistedOnboarding {
  version: typeof PERSISTED_ONBOARDING_VERSION;
  stage: OnboardingStage;
  subScreen: OnboardingSubScreen;
  displayName: string;
  handle: string;
  avatarUrl: string | null;
  heroUrl: string | null;
  phoneNumber: string;
  interestTopics: string[];
  interestPlatforms: string[];
}

const VALID_SUBSCREENS = ['1', '2', '3', '4', '5', '6', '7'] as const;

function toV2Stage(rawStage: number, rawVersion: number): number | null {
  if (rawStage < 1 || rawStage > 6) return null;
  if (rawVersion === 2) return rawStage;
  const legacyStage = rawStage >= 2 ? rawStage - 1 : 1;
  return legacyStage >= 4 ? legacyStage + 1 : legacyStage;
}

/**
 * Migrate persisted stage/subScreen onto the current 1–7 numbering.
 *
 * v1 (or unversioned): platform-settings was stage 1.
 * v2: connect → profile → topics → platforms → claim → simulation.
 * v3: inserts location after profile (stages ≥ 3 shift by 1).
 *
 * Returns null if the input is unrecognisable so the caller can fall back
 * to a fresh session.
 */
// ALLOW-UNKNOWN: helper narrows raw JSON.parse output from localStorage
function migrateLegacyStageAndSub(
  obj: Record<string, unknown>,
): { stage: OnboardingStage; subScreen: OnboardingSubScreen } | null {
  const rawStage = obj.stage;
  if (typeof rawStage !== 'number' || !Number.isInteger(rawStage)) return null;
  if (typeof obj.subScreen !== 'string') return null;
  const rawVersion = typeof obj.version === 'number' ? obj.version : 0;

  let stage: OnboardingStage;
  if (rawVersion >= PERSISTED_ONBOARDING_VERSION) {
    if (rawStage < 1 || rawStage > 7) return null;
    stage = rawStage as OnboardingStage;
  } else {
    const v2Stage = toV2Stage(rawStage, rawVersion);
    if (v2Stage === null) return null;
    stage = (v2Stage >= 3 ? v2Stage + 1 : v2Stage) as OnboardingStage;
  }

  const subScreen = String(stage) as OnboardingSubScreen;
  if (!(VALID_SUBSCREENS as readonly string[]).includes(subScreen)) return null;
  return { stage, subScreen };
}

// ALLOW-UNKNOWN: type guard takes `unknown` so callers can narrow JSON.parse output
function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
}

function readPersistedOnboarding(): PersistedOnboarding | null {
  if (typeof window === 'undefined') return null;
  try {
    // Read the user-scoped key first. Only fall back to the unscoped key when
    // morfi_user_id is genuinely missing — the unscoped fallback exists to
    // tolerate a race where the store hydrates before the user id is written.
    // If the user id IS present and the scoped key is missing, that user has
    // never started onboarding and MUST start fresh; reading the unscoped key
    // would leak a previous user's bailed onboarding state into this session.
    const uid = getCurrentUserId();
    const scoped = localStorage.getItem(stageKey());
    const raw =
      scoped ?? (uid === null || uid === '' ? localStorage.getItem(STORAGE_PREFIX_STAGE) : null);
    if (raw === null) return null;
    // ALLOW-UNKNOWN: JSON.parse returns unknown; narrowed via type guards below
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      !('stage' in parsed) ||
      !('subScreen' in parsed)
    ) {
      return null;
    }
    const obj = parsed as Record<string, unknown>;
    const migrated = migrateLegacyStageAndSub(obj);
    if (migrated === null) {
      return null;
    }
    // Form fields are optional in older blobs (back-compat with the previous
    // {stage, subScreen}-only schema) — default to empty/null and skip the
    // value if the persisted type is wrong.
    return {
      version: PERSISTED_ONBOARDING_VERSION,
      stage: migrated.stage,
      subScreen: migrated.subScreen,
      displayName: typeof obj.displayName === 'string' ? obj.displayName : '',
      handle: typeof obj.handle === 'string' ? obj.handle : '',
      avatarUrl: typeof obj.avatarUrl === 'string' ? obj.avatarUrl : null,
      heroUrl: typeof obj.heroUrl === 'string' ? obj.heroUrl : null,
      phoneNumber: typeof obj.phoneNumber === 'string' ? obj.phoneNumber : '',
      interestTopics: isStringArray(obj.interestTopics) ? obj.interestTopics : [],
      interestPlatforms: isStringArray(obj.interestPlatforms) ? obj.interestPlatforms : [],
    };
  } catch (err: unknown) {
    logger.warn('Failed to parse persisted onboarding state', {
      error: err instanceof Error ? err.message.slice(0, 200) : String(err),
    });
  }
  return null;
}

/** Read the full state and write the stage + form fields back to localStorage. */
function persistOnboarding(state: OnboardingState): void {
  if (typeof window === 'undefined') return;
  const blob: PersistedOnboarding = {
    version: PERSISTED_ONBOARDING_VERSION,
    stage: state.stage,
    subScreen: state.subScreen,
    displayName: state.displayName,
    handle: state.handle,
    // ALLOW-NSFW-STRIP: draft-form state — moderation runs after upload, not in form state
    avatarUrl: state.avatarUrl,
    // ALLOW-NSFW-STRIP: draft-form state — moderation runs after upload, not in form state
    heroUrl: state.heroUrl,
    phoneNumber: state.phoneNumber,
    interestTopics: state.interestTopics,
    interestPlatforms: state.interestPlatforms,
  };
  try {
    const value = JSON.stringify(blob);
    localStorage.setItem(stageKey(), value);
    localStorage.setItem(STORAGE_PREFIX_STAGE, value);
  } catch {
    // SILENT-CATCH: localStorage may be blocked or full; persistence is
    // best-effort and the user falls back to in-memory state.
  }
}

type FormFieldsSubset = Pick<
  OnboardingState,
  | 'displayName'
  | 'handle'
  | 'avatarUrl'
  | 'heroUrl'
  | 'phoneNumber'
  | 'interestTopics'
  | 'interestPlatforms'
>;

const EMPTY_FORM_FIELDS: FormFieldsSubset = {
  displayName: '',
  handle: '',
  avatarUrl: null,
  heroUrl: null,
  phoneNumber: '',
  interestTopics: [],
  interestPlatforms: [],
};

function getInitialState(): Pick<OnboardingState, 'stage' | 'subScreen'> & FormFieldsSubset {
  const persisted = readPersistedOnboarding();
  if (persisted !== null) {
    return {
      stage: persisted.stage,
      subScreen: persisted.subScreen,
      displayName: persisted.displayName,
      handle: persisted.handle,
      avatarUrl: persisted.avatarUrl,
      heroUrl: persisted.heroUrl,
      phoneNumber: persisted.phoneNumber,
      interestTopics: persisted.interestTopics,
      interestPlatforms: persisted.interestPlatforms,
    };
  }
  return { stage: 1, subScreen: '1', ...EMPTY_FORM_FIELDS };
}

const initial: Pick<OnboardingState, 'stage' | 'subScreen'> & FormFieldsSubset = getInitialState();

export const useOnboardingStore: UseBoundStore<StoreApi<OnboardingState>> =
  createTrackedStore<OnboardingState>('useOnboardingStore')((set, get) => ({
    stage: initial.stage,
    subScreen: initial.subScreen,
    startTime: Date.now(),
    stageStartTime: Date.now(),

    pendingLocale: null,
    pendingTheme: null,
    pendingCurrency: null,
    pendingCountry: null,

    displayName: initial.displayName,
    handle: initial.handle,
    avatarUrl: initial.avatarUrl,
    heroUrl: initial.heroUrl,
    phoneNumber: initial.phoneNumber,

    interestTopics: initial.interestTopics,
    interestPlatforms: initial.interestPlatforms,

    setSubScreen(sub: OnboardingSubScreen): void {
      const stageMap: Record<OnboardingSubScreen, OnboardingStage> = {
        '1': 1,
        '2': 2,
        '3': 3,
        '4': 4,
        '5': 5,
        '6': 6,
        '7': 7,
      };
      set({ stage: stageMap[sub], subScreen: sub });
      persistOnboarding(get());
    },

    /** Advance to stage 2 — profile (after connect socials). */
    advanceToStage2(): void {
      set({ stage: 2, subScreen: '2', stageStartTime: Date.now() });
      persistOnboarding(get());
    },

    /** Advance to stage 3 — location. */
    advanceToStage3(): void {
      set({ stage: 3, subScreen: '3', stageStartTime: Date.now() });
      persistOnboarding(get());
    },

    /** Advance to stage 4 — interest topics. */
    advanceToStage4(): void {
      set({ stage: 4, subScreen: '4', stageStartTime: Date.now() });
      persistOnboarding(get());
    },

    /** Advance to stage 5 — previously used platforms. */
    advanceToStage5(): void {
      set({ stage: 5, subScreen: '5', stageStartTime: Date.now() });
      persistOnboarding(get());
    },

    /** Advance to stage 6 — claim devnet. */
    advanceToStage6(): void {
      set({ stage: 6, subScreen: '6', stageStartTime: Date.now() });
      persistOnboarding(get());
    },

    /** Advance to stage 7 — simulation rehearsal. */
    advanceToStage7(): void {
      set({ stage: 7, subScreen: '7', stageStartTime: Date.now() });
      persistOnboarding(get());
    },

    goBackToStage1(): void {
      set({ stage: 1, subScreen: '1', stageStartTime: Date.now() });
      persistOnboarding(get());
    },

    goBackToStage2(): void {
      set({ stage: 2, subScreen: '2', stageStartTime: Date.now() });
      persistOnboarding(get());
    },

    goBackToStage3(): void {
      set({ stage: 3, subScreen: '3', stageStartTime: Date.now() });
      persistOnboarding(get());
    },

    goBackToStage4(): void {
      set({ stage: 4, subScreen: '4', stageStartTime: Date.now() });
      persistOnboarding(get());
    },

    goBackToStage5(): void {
      set({ stage: 5, subScreen: '5', stageStartTime: Date.now() });
      persistOnboarding(get());
    },

    rehydratePersistedProgress(): void {
      // The module-level `getInitialState()` runs before login has written
      // `morfi_user_id`, so the user-scoped stage key (which survives logout
      // — see the SDK's `clearUserStorage`) is invisible to it. Once the
      // authed user is known, re-read the persisted progress and resume at
      // the stage they reached instead of restarting from stage 1
      // (MOR-1784). Only applies onto a still-fresh store so it can never
      // rewind live in-session progress.
      const { stage, subScreen } = get();
      if (stage !== 1 || subScreen !== '1') return;
      const persisted = readPersistedOnboarding();
      if (persisted === null || (persisted.stage === 1 && persisted.subScreen === '1')) return;
      set({
        stage: persisted.stage,
        subScreen: persisted.subScreen,
        displayName: persisted.displayName,
        handle: persisted.handle,
        // ALLOW-NSFW-STRIP: draft-form state — moderation runs after upload, not in form state
        avatarUrl: persisted.avatarUrl,
        // ALLOW-NSFW-STRIP: draft-form state — moderation runs after upload, not in form state
        heroUrl: persisted.heroUrl,
        phoneNumber: persisted.phoneNumber,
        interestTopics: persisted.interestTopics,
        interestPlatforms: persisted.interestPlatforms,
        stageStartTime: Date.now(),
      });
    },

    completeOnboarding(): void {
      if (typeof window !== 'undefined') {
        // Write to both scoped and unscoped keys so the completion state
        // is found regardless of whether morfi_user_id is in localStorage.
        localStorage.setItem(completedKey(), 'true');
        localStorage.setItem(STORAGE_PREFIX_COMPLETED, 'true');
        localStorage.removeItem(stageKey());
        localStorage.removeItem(STORAGE_PREFIX_STAGE);
      }
    },

    setPendingLocale(locale: Locale): void {
      set({ pendingLocale: locale });
    },

    setPendingTheme(theme: 'light' | 'dark'): void {
      set({ pendingTheme: theme });
    },

    setPendingCurrency(code: string): void {
      set({ pendingCurrency: code });
    },

    setPendingCountry(code: string): void {
      set({ pendingCountry: code });
    },

    setDisplayName(name: string): void {
      set({ displayName: name });
      persistOnboarding(get());
    },

    setHandle(handle: string): void {
      set({ handle });
      persistOnboarding(get());
    },

    setAvatarUrl(url: string | null): void {
      set({ avatarUrl: url });
      persistOnboarding(get());
    },

    setHeroUrl(url: string | null): void {
      set({ heroUrl: url });
      persistOnboarding(get());
    },

    setPhoneNumber(phone: string): void {
      set({ phoneNumber: phone });
      persistOnboarding(get());
    },

    toggleInterestTopic(topicId: string): void {
      const current = get().interestTopics;
      if (current.includes(topicId)) {
        set({ interestTopics: current.filter((t) => t !== topicId) });
      } else {
        set({ interestTopics: [...current, topicId] });
      }
      persistOnboarding(get());
    },

    toggleInterestPlatform(platformId: string): void {
      const current = get().interestPlatforms;
      if (current.includes(platformId)) {
        set({ interestPlatforms: current.filter((p) => p !== platformId) });
      } else {
        set({ interestPlatforms: [...current, platformId] });
      }
      persistOnboarding(get());
    },

    setInterestTopics(topics: string[]): void {
      set({ interestTopics: topics });
      persistOnboarding(get());
    },

    setInterestPlatforms(platforms: string[]): void {
      set({ interestPlatforms: platforms });
      persistOnboarding(get());
    },

    reset(): void {
      if (typeof window !== 'undefined') {
        localStorage.removeItem(stageKey());
        localStorage.removeItem(STORAGE_PREFIX_STAGE);
      }
      set({
        stage: 1,
        subScreen: '1',
        startTime: Date.now(),
        stageStartTime: Date.now(),
        pendingLocale: null,
        pendingTheme: null,
        pendingCurrency: null,
        pendingCountry: null,
        displayName: '',
        handle: '',
        avatarUrl: null,
        heroUrl: null,
        phoneNumber: '',
        interestTopics: [],
        interestPlatforms: [],
      });
    },
  }));

export function isOnboardingCompleted(): boolean {
  if (typeof window === 'undefined') return false;
  // Same scoping rule as readPersistedOnboarding: only fall back to the
  // unscoped key when morfi_user_id is missing (the race the fallback was
  // designed for). Reading the unscoped key when a different user_id IS
  // set would treat user B as "already completed" because user A finished
  // onboarding earlier in the same browser.
  const uid = getCurrentUserId();
  if (localStorage.getItem(completedKey()) === 'true') return true;
  if (uid !== null && uid !== '') return false;
  return localStorage.getItem(STORAGE_PREFIX_COMPLETED) === 'true';
}

// ── Durable backend-persist flag (MOR-1784) ─────────────────────────────────
// The backend write of `onboardingCompleted` must survive a transient failure
// (a 401 during a session rotation loses a fire-and-forget PATCH forever, and
// logout wipes the local completion record — the re-onboarding loop). The
// flag is set at completion, cleared only when the PATCH is ACKNOWLEDGED, and
// retried by `<OnboardingCompletionSync>` on every authenticated load.

const STORAGE_PREFIX_PERSIST_PENDING = 'onboarding-persist-pending';

function persistPendingKey(): string {
  const uid = getCurrentUserId();
  return uid ? `${STORAGE_PREFIX_PERSIST_PENDING}:${uid}` : STORAGE_PREFIX_PERSIST_PENDING;
}

export function markOnboardingPersistPending(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(persistPendingKey(), 'true');
    localStorage.setItem(STORAGE_PREFIX_PERSIST_PENDING, 'true');
  } catch {
    // SILENT-CATCH: localStorage may be blocked; the immediate PATCH attempt
    // still runs, we only lose the retry safety net.
  }
}

export function isOnboardingPersistPending(): boolean {
  if (typeof window === 'undefined') return false;
  const uid = getCurrentUserId();
  if (localStorage.getItem(persistPendingKey()) === 'true') return true;
  if (uid !== null && uid !== '') return false;
  return localStorage.getItem(STORAGE_PREFIX_PERSIST_PENDING) === 'true';
}

export function clearOnboardingPersistPending(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(persistPendingKey());
    localStorage.removeItem(STORAGE_PREFIX_PERSIST_PENDING);
  } catch {
    // SILENT-CATCH: localStorage may be blocked.
  }
}
